import React from "react";
import {
  SignalRProvider as SharedSignalRProvider,
  setDebugLoggerFactory,
  useSignalR,
  type SignalRContextValue,
} from "@onetasklist/shared";
import { createDebugLogger } from "@utils/debugLog";
import { subscribeToAppResume } from "@utils/appResume";
import { useAuth } from "./AuthContext";
import { oidcAuthClient } from "./OidcAuthClient";

// Routes @onetasklist/shared's SignalR debug logging (connection lifecycle,
// board-group join/leave, notification receipt) through the `debug` package
// so it can be toggled at runtime via localStorage.debug, e.g.:
//   localStorage.debug = "otl:signalr:*"
setDebugLoggerFactory(createDebugLogger);

// Asked on every negotiate, so a reconnect always carries a current token
// (refreshed first if it lapsed while the machine slept). Never waits for the
// user: if the refresh is refused, the client reports it and the auth context
// asks the user to sign in again in a popup. The renewed token then reconnects
// the hub (below), and rejoining the board group refetches what was missed.
const getHubAccessToken = async (): Promise<string | null> => {
  const result = await oidcAuthClient.requestAccessToken();
  return result.kind === "token" ? result.accessToken : null;
};

// A renewed token proves the network and the identity server are back, so it
// is as good a cue to reconnect as the tab coming back into view.
const subscribeToHubResume = (onResume: () => void): (() => void) => {
  const unsubscribeAppResume = subscribeToAppResume(onResume);
  const unsubscribeTokenRenewed = oidcAuthClient.onTokenRenewed(() => onResume());

  return () => {
    unsubscribeAppResume();
    unsubscribeTokenRenewed();
  };
};

export interface SignalRProviderProps {
  children: React.ReactNode;
}

export const SignalRProvider: React.FC<SignalRProviderProps> = ({
  children,
}) => {
  const { isAuthenticated, regionBaseUrl } = useAuth();
  const hubUrl = regionBaseUrl ? `${regionBaseUrl}/boardHub` : null;

  return (
    <SharedSignalRProvider
      hubUrl={hubUrl}
      isAuthenticated={isAuthenticated}
      getAccessToken={getHubAccessToken}
      subscribeToResume={subscribeToHubResume}
      autoReconnect
    >
      {children}
    </SharedSignalRProvider>
  );
};

export { useSignalR, type SignalRContextValue };
